import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface StoredDraft<T> {
  version: 1;
  savedAt: string;
  value: T;
}

/** Keeps unfinished editor work in this browser, isolated by account, mode and map. */
export function useEditorDraft<T>({
  storageKey,
  ready,
  value,
  onRestore,
  isValid,
}: {
  storageKey: string;
  ready: boolean;
  value: T;
  onRestore: (value: T) => void;
  isValid: (value: unknown) => value is T;
}) {
  const serialised = useMemo(() => JSON.stringify(value), [value]);
  const session = useRef({ key: '', baseline: '', pending: false });
  const supersededKey = useRef<string | null>(null);
  const latest = useRef({ value, serialised });
  latest.current = { value, serialised };
  const [pending, setPending] = useState<StoredDraft<T> | null>(null);
  const [baseline, setBaseline] = useState(serialised);
  const [notice, setNotice] = useState<string | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);

  const remove = useCallback((key: string) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Could not remove editor draft:', error);
      setStorageError('Browser recovery is unavailable. Save your map before leaving.');
      return false;
    }
  }, []);

  const persist = useCallback((key: string, draftValue: T) => {
    try {
      const draft: StoredDraft<T> = { version: 1, savedAt: new Date().toISOString(), value: draftValue };
      localStorage.setItem(key, JSON.stringify(draft));
      setStorageError(null);
    } catch (error) {
      console.error('Could not back up editor draft:', error);
      setStorageError('Browser recovery is unavailable. Save your map before leaving.');
    }
  }, []);

  useEffect(() => {
    if (session.current.key === storageKey) {
      supersededKey.current = null;
      return;
    }
    if (!ready || supersededKey.current === storageKey) {
      return;
    }
    session.current = { key: storageKey, baseline: serialised, pending: false };
    setBaseline(serialised);
    setPending(null);
    setNotice(null);
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const draft = JSON.parse(raw) as StoredDraft<unknown>;
        if (draft.version !== 1 || typeof draft.savedAt !== 'string' ||
          !Number.isFinite(Date.parse(draft.savedAt)) || !isValid(draft.value)) {
          throw new Error('Invalid editor draft');
        }
        if (JSON.stringify(draft.value) !== serialised) {
          session.current.pending = true;
          setPending(draft as StoredDraft<T>);
        } else {
          remove(storageKey);
        }
      }
    } catch (error) {
      console.error('Could not recover editor draft:', error);
      setStorageError('The browser draft could not be read. Save your map before leaving.');
    }
  }, [isValid, ready, remove, serialised, storageKey]);

  const isDirty = ready && session.current.key === storageKey && serialised !== baseline;
  useEffect(() => {
    if (!ready || session.current.pending || session.current.key !== storageKey) {
      return;
    }
    if (serialised !== session.current.baseline) {
      persist(storageKey, latest.current.value);
    } else {
      remove(storageKey);
    }
  }, [pending, persist, ready, remove, serialised, storageKey]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const restore = () => {
    if (pending) {
      onRestore(pending.value);
      session.current.pending = false;
      setPending(null);
      setNotice('Draft restored. Save when you are ready.');
    }
  };

  const discard = () => {
    if (remove(storageKey)) {
      session.current.pending = false;
      setPending(null);
      setNotice(null);
    }
  };

  const markSaved = useCallback((savedValue: T, nextKey = storageKey) => {
    const saved = JSON.stringify(savedValue);
    remove(storageKey);
    supersededKey.current = nextKey !== storageKey ? storageKey : null;
    session.current = { key: nextKey, baseline: saved, pending: false };
    setBaseline(saved);
    setNotice(null);
    if (latest.current.serialised !== saved) {
      persist(nextKey, latest.current.value);
    } else {
      remove(nextKey);
    }
  }, [persist, remove, storageKey]);

  return { pending, isDirty, notice, storageError, restore, discard, markSaved };
}
